/* Feb-2026 · Synopsis Tools hub — "merge the scattered synopsis tools
 * into a single, obvious entry point" (user request). The File → Setup
 * menu used to list 4 separate synopsis-related MenuItems; this single
 * dialog now groups them as plain-language cards. Multiple doors still
 * lead here (Setup menu + the AI Assist dropdown's "Generate Synopsis"
 * opens the manuscript-synopsis door directly) but it's the same one
 * dialog/decision point either way — no functionality was removed,
 * each card still calls the exact same existing handler it always did.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Wand2, ScrollText, FileStack, NotebookPen, Sparkles } from 'lucide-react';
import { setPendingSynopsisOpen } from '@/lib/pendingIntents';

const CARDS = [
  {
    id: 'logline',
    icon: Wand2,
    title: 'I have a one-line idea',
    body: 'Turn a short idea into a full synopsis.',
    action: 'onGenerateSynopsisFromLogline',
  },
  {
    id: 'has-synopsis',
    icon: ScrollText,
    title: 'I already have a synopsis',
    body: 'Use it to set up a new story — chapters, cast, and outline.',
    action: 'onImportSynopsis',
  },
  {
    id: 'bible-import',
    icon: FileStack,
    title: 'Populate my Character Bible from a synopsis',
    body: "Import a 3-act synopsis and Claude fills in the cast it finds.",
    action: 'onImportThreeActSynopsis',
  },
  {
    id: 'builder',
    icon: NotebookPen,
    title: "Build one, step by step",
    body: 'An 11-step guided builder if you want help shaping it from scratch.',
    action: 'onBuildSynopsis',
  },
  {
    id: 'from-draft',
    icon: ScrollText,
    title: "See a synopsis of what I've already written",
    body: 'Reads your drafted chapters so far and summarizes the story.',
    action: 'onFromDraft',
  },
  {
    id: 'metadata-extract',
    icon: Sparkles,
    title: 'Extract all metadata from this manuscript',
    body: 'AI reads your chapters and fills in characters, world bible, codex, plot beats, genre, tone, and more.',
    action: 'onMetadataExtract',
  },
];

export default function SynopsisToolsHub({
  open, onOpenChange, onGenerateSynopsisFromLogline, onImportSynopsis,
  onImportThreeActSynopsis, onBuildSynopsis, togglePanel, onMetadataExtract,
}) {
  const handlers = {
    onGenerateSynopsisFromLogline,
    onImportSynopsis,
    onImportThreeActSynopsis,
    onBuildSynopsis,
    // The dialog this opens lives inside the editor panel — make sure
    // it's actually mounted (e.g. the Home Launcher may be showing
    // instead) before firing the open event.
    onFromDraft: () => {
      setPendingSynopsisOpen();
      togglePanel?.('home', false);
      togglePanel?.('editor', true);
      window.dispatchEvent(new CustomEvent('loom:open-manuscript-synopsis'));
    },
    onMetadataExtract,
  };

  const pick = (card) => {
    onOpenChange(false);
    handlers[card.action]?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="synopsis-tools-hub">
        <DialogHeader>
          <DialogTitle>Synopsis Tools</DialogTitle>
          <DialogDescription>Pick whichever matches where you're starting from.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.id}
                type="button"
                onClick={() => pick(card)}
                data-testid={`synopsis-hub-${card.id}`}
                className="flex w-full items-start gap-3 rounded-lg border border-border p-3 text-left transition hover:border-primary hover:bg-primary/5"
              >
                <Icon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <span>
                  <span className="block text-[13px] font-semibold text-foreground">{card.title}</span>
                  <span className="block text-[12px] text-muted-foreground">{card.body}</span>
                </span>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
